import React, { useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Card,
  Button,
  Space
} from 'antd';
import dayjs from 'dayjs';
import { VoucherFormData, Voucher, VoucherType } from '../../types';

const { Option } = Select;
const { TextArea } = Input;

interface VoucherFormProps {
  initialValues?: Partial<Voucher>;
  onFinish: (values: VoucherFormData) => void;
}

const VoucherForm: React.FC<VoucherFormProps> = ({
  initialValues,
  onFinish
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (initialValues) {
      const formValues = {
        ...initialValues,
        expires_at: initialValues.expires_at ? dayjs(initialValues.expires_at) : null,
      };
      form.setFieldsValue(formValues);
    }
  }, [initialValues, form]);

  const handleFinish = (values: any) => {
    const formData: VoucherFormData = {
      code: values.code,
      voucher_type: values.voucher_type,
      value: values.value,
      description: values.description,
      expires_at: values.expires_at ? values.expires_at.toISOString() : undefined,
    };
    onFinish(formData);
  };

  const voucherTypeOptions = [
    { value: VoucherType.VOUCHER_10K, label: '10K VNĐ' },
    { value: VoucherType.VOUCHER_20K, label: '20K VNĐ' },
    { value: VoucherType.VOUCHER_50K, label: '50K VNĐ' },
    { value: VoucherType.VOUCHER_100K, label: '100K VNĐ' },
    { value: VoucherType.VOUCHER_200K, label: '200K VNĐ' },
    { value: VoucherType.VOUCHER_300K, label: '300K VNĐ' },
    { value: VoucherType.VOUCHER_500K, label: '500K VNĐ' },
    { value: VoucherType.VOUCHER_1M, label: '1M VNĐ' },
    { value: VoucherType.VOUCHER_2M, label: '2M VNĐ' }
  ];

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{
        voucher_type: VoucherType.VOUCHER_10K,
      }}
    >
      <Card title="Thông tin voucher" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="code"
              label="Mã voucher"
              rules={[
                { required: true, message: 'Vui lòng nhập mã voucher' },
                { min: 3, message: 'Mã voucher phải có ít nhất 3 ký tự' },
                { max: 50, message: 'Mã voucher không được quá 50 ký tự' }
              ]}
            >
              <Input
                placeholder="VD: VOUCHER10K, WELCOME2024"
                style={{ textTransform: 'uppercase' }}
                onChange={(e) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="voucher_type"
              label="Loại voucher"
              rules={[{ required: true, message: 'Vui lòng chọn loại voucher' }]}
            >
              <Select placeholder="Chọn loại voucher">
                {voucherTypeOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="value"
              label="Giá trị (VNĐ)"
              rules={[
                { required: true, message: 'Vui lòng nhập giá trị' },
                { type: 'number' as const, min: 1, message: 'Giá trị phải lớn hơn 0' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="VD: 10000"
                formatter={(value: any) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value: any) => value?.replace(/[^\d]/g, '') || ''}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="expires_at"
              label="Ngày hết hạn"
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Chọn ngày hết hạn"
                showTime
                format="DD/MM/YYYY HH:mm"
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Mô tả"
          rules={[
            { max: 255, message: 'Mô tả không được quá 255 ký tự' }
          ]}
        >
          <TextArea
            rows={3}
            placeholder="Mô tả chi tiết về voucher này..."
          />
        </Form.Item>
      </Card>

      <div style={{ textAlign: 'right', marginTop: 16 }}>
        <Space>
          <Button htmlType="submit" type="primary">
            Lưu voucher
          </Button>
        </Space>
      </div>
    </Form>
  );
};

export default VoucherForm;
